import useCampaigns from '../useCampaigns';
import { isNumber } from 'utility/utility';
import moment from 'moment';
import uniq from 'lodash/uniq';
import { useInstagramAudios, useInstagramTimeSeries } from '@round/platforms/instagram';
import useNonNullContext from 'Hooks/useNonNullContext';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import { buildInstagramAudioUrl } from 'helpers';
import { CampaignTableRow } from '../CampaignsTable/ReportCampaignsTable';

export function useReportInstagramCampaigns() {
    const { currencies } = useNonNullContext(OptionsContext);
    const {
        campaignsData,
        projectsData,
        brandsData,
        teamsData,
        campaignStatsData,
        isTeamsLoading,
        isProjectsLoading,
        getIsBrandLoading,
        getIsCampaignStatsLoading,
    } = useCampaigns('instagram');

    const audioIds = uniq(
        campaignsData.data?.results.map((campaign) => campaign.instagram_details?.audio_id).filter(isNumber) ?? []
    );

    const { data: instagramAudiosData } = useInstagramAudios(
        { id: audioIds.join(','), page_size: audioIds.length },
        {
            enabled: audioIds.length > 0,
            select: (data) => new Map(data.results.map((audio) => [audio.id, audio])),
        }
    );

    const {
        data: audioTimeSeriesData,
        isLoading: isAudioTimeSeriesLoading,
        isError: isAudioTimeSeriesError,
    } = useInstagramTimeSeries(
        {
            type: 'audio',
            ids: audioIds,
            fields: ['posts_daily_change'],
            start_date: moment().subtract(14, 'days').format('YYYY-MM-DD'),
            end_date: moment().format('YYYY-MM-DD'),
        },
        { enabled: audioIds.length > 0 }
    );

    const rows: CampaignTableRow[] =
        campaignsData.data?.results?.map((campaign) => {
            const project = projectsData.get(campaign.project_id) ?? null;
            const brand = (project && brandsData[project.brand_id]?.data) || null;
            const team = project ? teamsData?.get(project.team_id) ?? null : null;
            const currency = currencies.find((c) => c.id === campaign.currency_id);
            const audioId = campaign.instagram_details?.audio_id;
            const audio = audioId ? instagramAudiosData?.get(audioId) ?? null : null;
            const audioUrl = audio?.instagram_id ? buildInstagramAudioUrl(audio.instagram_id) : undefined;
            const audioTimeSeries = audioId ? audioTimeSeriesData?.[audioId] ?? null : null;

            return {
                ...campaign,
                stats: campaignStatsData[campaign.id]?.data ?? null,
                project,
                team,
                brand,
                currency,
                audioUrl,
                audioTimeSeries,
            };
        }) ?? [];

    return {
        rows,
        isLoading: campaignsData.isLoading,
        isTeamsLoading,
        isProjectsLoading,
        isAudioTimeSeriesLoading,
        isAudioTimeSeriesError,
        getIsBrandLoading,
        getIsCampaignStatsLoading,
    };
}
